import { useEffect, useRef, useState } from "react";
import {
  SignInButton,
  SignedIn,
  SignedOut,
  useClerk,
} from "@clerk/clerk-react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

const LEVELS = ["full", "half", "low", "empty"];
const LEVEL_LABEL = { full: "Full", half: "Half", low: "Low", empty: "Out" };
const PLACES = ["pantry", "fridge", "freezer"];
const titleCase = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const chf = (cents) => (cents / 100).toFixed(2);

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* palette slot per member, so the same person keeps the same colour */
const hueFor = (name, members) => {
  const i = members.findIndex((m) => m.name === name);
  return i <= 0 ? "nk" : "sa";
};

const ICONS = {
  all: <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />,
  list: <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />,
  pantry: <path d="M5 4h14v16H5zM5 12h14M9 8.5h.01M9 16h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />,
  recipes: <><path d="M5 4.5h11a2 2 0 012 2v13H7a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" /><path d="M9 9h6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></>,
  orders: <path d="M6 8h12l-1 11H7zM9 8a3 3 0 016 0" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />,
  home: <path d="M4 10.5L12 4l8 6.5V19a1 1 0 01-1 1H5a1 1 0 01-1-1z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />,
  basket: <><circle cx="10" cy="20" r="1.4" fill="currentColor" /><circle cx="17" cy="20" r="1.4" fill="currentColor" /><path d="M3 4h2.2l2.3 11.2a1.5 1.5 0 001.5 1.2h8.1a1.5 1.5 0 001.5-1.2L20.5 8H6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></>,
  receipts: <><path d="M6 3.5h12v17l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" /><path d="M9.5 8h5M9.5 12h5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></>,
};

const Icon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">{ICONS[name]}</svg>
);

const CHEV = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const GRIP = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    {[6, 12, 18].flatMap((cy) => [
      <circle key={`l${cy}`} cx="9" cy={cy} r="1.6" />,
      <circle key={`r${cy}`} cx="15" cy={cy} r="1.6" />,
    ])}
  </svg>
);
const EX = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" /></svg>
);

/* ---------------------------------------------------------------- card shell */

function Card({ id, title, badge, children, layout, onLayout, drag }) {
  const collapsed = layout.collapsed.includes(id);

  return (
    <article
      className={`card${collapsed ? " card--collapsed" : ""}${drag.over === id ? " card--over" : ""}${drag.key === id ? " card--dragging" : ""}`}
      draggable={drag.armed === id}
      onDragStart={(e) => { drag.start(id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={drag.end}
      onDragOver={(e) => { if (drag.key && drag.key !== id) { e.preventDefault(); drag.setOver(id); } }}
      onDragLeave={() => drag.setOver(null)}
      onDrop={(e) => { e.preventDefault(); drag.drop(id); }}
    >
      <div className="card-head">
        <h2 className="card-title">{title}</h2>
        {badge}
        <div className="tools">
          <button
            className="tool tool--grip"
            type="button"
            aria-label="Drag to reorder"
            onMouseDown={() => drag.arm(id)}
            onMouseUp={() => drag.arm(null)}
          >{GRIP}</button>
          <button
            className="tool tool--min"
            type="button"
            aria-label={collapsed ? "Expand" : "Minimise"}
            onClick={() => onLayout({ ...layout, collapsed: toggle(layout.collapsed, id) })}
          >{CHEV}</button>
          <button
            className="tool tool--close"
            type="button"
            aria-label="Close"
            onClick={() => onLayout({ ...layout, closed: [...layout.closed, id] })}
          >{EX}</button>
        </div>
      </div>
      <div className="card-body">{children}</div>
    </article>
  );
}

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

/* --------------------------------------------------------------------- cards */

function ShoppingCard(props) {
  const { items, members, lists, list, onPickList } = props;
  const addItem = useMutation(api.web.addItem);
  const setChecked = useMutation(api.web.setItemChecked);
  const removeItem = useMutation(api.web.removeItem);
  const [draft, setDraft] = useState("");

  const commit = () => {
    const name = draft.trim();
    if (!name) return;
    addItem({ name });
    setDraft("");
  };

  return (
    <Card {...props} id="list" title={list?.name || "Shopping list"}
          badge={<span className="badge badge--soft">{items.length} items</span>}>
      {lists.length > 1 && (
        <select
          className="picker"
          value={list?._id || ""}
          aria-label="Which list"
          onChange={(e) => onPickList(e.target.value)}
        >
          {lists.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
        </select>
      )}
      <ul className="rows">
        {items.map((i) => (
          <li key={i._id} className="row row--line">
            <input
              className="tick"
              type="checkbox"
              id={i._id}
              checked={i.checked || i.ordered}
              disabled={i.ordered}
              onChange={(e) => setChecked({ itemId: i._id, checked: e.target.checked })}
              aria-label={i.name}
            />
            <label className="name" htmlFor={i._id}>
              {i.name}
              <span className="sub">{i.quantity || i.description || "—"}</span>
            </label>
            <span className={`by by--${hueFor(i.addedBy, members)}`} title={`Added by ${i.addedBy}`}>
              {initials(i.addedBy)}
            </span>
            {i.ordered
              ? <span className="chip-ordered">Ordered</span>
              : <span className="price" />}
            <button className="kill" type="button" aria-label={`Remove ${i.name}`}
                    onClick={() => removeItem({ itemId: i._id })}>✕</button>
          </li>
        ))}
      </ul>

      {!items.length && <p className="empty">Nothing on the list yet.</p>}

      <div className="adder">
        <input
          type="text"
          value={draft}
          placeholder="Add something to the list"
          aria-label="Add to list"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        />
        <button type="button" onClick={commit}>Add</button>
      </div>
    </Card>
  );
}

function KitchenCard(props) {
  const { pantry } = props;
  const [showAll, setShowAll] = useState(false);
  const [openFor, setOpenFor] = useState(null);   // which row's level menu is open
  const [openWhere, setOpenWhere] = useState(null); // which row's location menu is open
  const [place, setPlace] = useState("all");        // location filter
  const CAP = 40;
  const addStock = useMutation(api.web.addStock);
  const setLevel = useMutation(api.web.setStockLevel);
  const setWhere = useMutation(api.web.setStockLocation);
  const removeStock = useMutation(api.web.removeStock);
  const addItem = useMutation(api.web.addItem);
  const [draft, setDraft] = useState("");

  const short = pantry.filter((p) => p.level === "low" || p.level === "empty").length;

  // Locations really exist in the data — surface them instead of hiding them
  // in a subtitle.
  const places = [...new Set(pantry.map((p) => p.where).filter(Boolean))].sort();
  const inPlace = place === "all" ? pantry : pantry.filter((p) => p.where === place);
  const shown = showAll ? inPlace : inPlace.slice(0, CAP);

  const commit = () => {
    const name = draft.trim();
    if (!name) return;
    addStock({ name, location: place === "all" ? "pantry" : place, level: "full" });
    setDraft("");
  };

  return (
    <Card {...props} id="pantry" title="Pantry"
          badge={<span className={`badge${short ? "" : " badge--soft"}`}>
            {short ? `${short} running out` : "All stocked"}
          </span>}>
      {places.length > 1 && (
        <div className="chips places">
          <button className={`chip${place === "all" ? " chip--on" : ""}`} type="button"
                  onClick={() => setPlace("all")}>All {pantry.length}</button>
          {places.map((pl) => (
            <button key={pl} className={`chip${place === pl ? " chip--on" : ""}`} type="button"
                    onClick={() => setPlace(pl)}>
              {titleCase(pl)} {pantry.filter((x) => x.where === pl).length}
            </button>
          ))}
        </div>
      )}
      <ul className="rows">
        {shown.map((p) => (
          <li key={p._id} className="row row--pot">
            <span className="name">
              {p.name}
              <span className="sub">
                <span className="where-wrap"
                      onMouseEnter={() => setOpenWhere(p._id)}
                      onMouseLeave={() => setOpenWhere((c) => (c === p._id ? null : c))}>
                  <button className="where" type="button" aria-haspopup="true"
                          aria-expanded={openWhere === p._id}
                          onClick={() => setOpenWhere(openWhere === p._id ? null : p._id)}>
                    {titleCase(p.where)}
                  </button>
                  {openWhere === p._id && (
                    <div className="level-menu" role="menu">
                      {[...new Set([...PLACES, ...places])].map((pl) => (
                        <button key={pl} role="menuitem" type="button"
                                className={`level-opt where-opt${pl === p.where ? " is-current" : ""}`}
                                onClick={() => {
                                  if (pl !== p.where) setWhere({ stockId: p._id, location: pl });
                                  setOpenWhere(null);
                                }}>
                          {titleCase(pl)}
                        </button>
                      ))}
                    </div>
                  )}
                </span>
                {p.description ? `, ${p.description}` : ""}
              </span>
            </span>
            <span
              className="level-wrap"
              onMouseEnter={() => setOpenFor(p._id)}
              onMouseLeave={() => setOpenFor((cur) => (cur === p._id ? null : cur))}
            >
              <button
                className={`level level--${p.level}`}
                type="button"
                aria-haspopup="true"
                aria-expanded={openFor === p._id}
                onClick={() => setOpenFor(openFor === p._id ? null : p._id)}
                onKeyDown={(e) => { if (e.key === "Escape") setOpenFor(null); }}
              >{LEVEL_LABEL[p.level]}</button>

              {openFor === p._id && (
                <div className="level-menu" role="menu">
                  {LEVELS.map((lv) => (
                    <button
                      key={lv}
                      className={`level-opt level--${lv}${lv === p.level ? " is-current" : ""}`}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        if (lv !== p.level) setLevel({ stockId: p._id, level: lv });
                        setOpenFor(null);
                      }}
                    >
                      {LEVEL_LABEL[lv]}
                    </button>
                  ))}
                </div>
              )}
            </span>
            <button className="ghost" type="button"
                    onClick={() => addItem({ name: p.name, quantity: "from the kitchen" })}>
              Add to list
            </button>
            <button className="kill" type="button" aria-label={`Remove ${p.name}`}
                    onClick={() => removeStock({ stockId: p._id })}>✕</button>
          </li>
        ))}
      </ul>

      {!inPlace.length && (
        <p className="empty">
          {place === "all"
            ? "Nothing tracked yet. Add what you have at home."
            : `Nothing in the ${place} yet.`}
        </p>
      )}

      {inPlace.length > CAP && (
        <button className="restore" type="button" style={{ marginTop: 10 }}
                onClick={() => setShowAll(!showAll)}>
          {showAll ? `Show only the first ${CAP}` : `Show all ${inPlace.length}`}
        </button>
      )}

      <div className="adder">
        <input
          type="text"
          value={draft}
          placeholder={place === "all" ? "Add something to the pantry" : `Add to the ${place}`}
          aria-label="Add to kitchen"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        />
        <button type="button" onClick={commit}>Add</button>
      </div>
    </Card>
  );
}

function RecipeCard({ recipe, ...props }) {
  return (
    <Card {...props} id={`recipe:${recipe._id}`} title={recipe.name}
          badge={<span className="badge">{recipe.ingredients.length} things</span>}>
      <ul className="ings">
        {recipe.ingredients.map((ing, n) => (
          <li key={n}>
            {ing.quantity && <b>{ing.quantity}{ing.unit ? ` ${ing.unit}` : ""}</b>} {ing.name}
          </li>
        ))}
      </ul>
      <ol className="steps">
        {recipe.steps.map((s, n) => <li key={n}>{s.instruction}</li>)}
      </ol>
    </Card>
  );
}

function OrderCard({ order, members, ...props }) {
  const when = order.deliveryFrom
    ? new Date(order.deliveryFrom).toLocaleString([], {
        weekday: "short", hour: "2-digit", minute: "2-digit",
      })
    : "Not scheduled";

  return (
    <Card {...props} id={`order:${order._id}`} title="Order placed"
          badge={
            <span className="badge">
              <span className={`by by--${hueFor(order.by, members)}`}
                    style={{ display: "inline-grid", verticalAlign: "-4px", marginRight: 5 }}>
                {initials(order.by)}
              </span>
              {order.byYou ? "You" : order.by}
            </span>
          }>
      <dl className="facts">
        <div className="fact"><dt>Delivery</dt><dd>{when}</dd></div>
        {order.deliveryAddress && (
          <div className="fact"><dt>Address</dt><dd>{order.deliveryAddress}</dd></div>
        )}
        <div className="fact"><dt>Items</dt><dd>{order.itemCount}</dd></div>
        {order.totalCents != null && (
          <div className="fact"><dt>Total</dt><dd>CHF {chf(order.totalCents)}</dd></div>
        )}
        <div className="fact"><dt>Status</dt><dd>{order.status.replace(/_/g, " ")}</dd></div>
      </dl>
    </Card>
  );
}

function Attachment({ m }) {
  if (!m.mediaUrl) return null;
  if (m.mediaType === "image") {
    return <img className="shot" src={m.mediaUrl} alt={m.content || "Shared photo"} />;
  }
  if (m.mediaType === "audio") {
    return <audio className="memo" src={m.mediaUrl} controls preload="none" />;
  }
  return <a className="filelink" href={m.mediaUrl} target="_blank" rel="noreferrer">Open attachment</a>;
}

/* The live Migros basket. Fetched on demand through the agent service — it
   lives at the store, not in our database, so there is nothing to subscribe to. */
function BasketCard(props) {
  const fetchBasket = useAction(api.agent.basket);
  const [state, setState] = useState({ loading: true });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await fetchBasket();
      setState({ loading: false, ...r });
    } catch (e) {
      setState({ loading: false, ok: false, error: String(e.message || e) });
    }
  };

  useEffect(() => { load(); }, []);

  const items = state.items ?? [];
  const total = items.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
  const MINIMUM = 99;
  const met = total >= MINIMUM;
  const slot = state.summary?.delivery_slot;

  return (
    <Card {...props} id="basket" title="Migros basket"
          badge={<span className="badge badge--soft">
            {state.loading ? "checking…" : `${items.length} items`}
          </span>}>
      {state.loading && <p className="empty">Reading your basket at Migros…</p>}

      {!state.loading && state.ok === false && (
        <p className="empty">Couldn&apos;t reach your basket. {state.error}</p>
      )}

      {!state.loading && state.ok && (
        <>
          <ul className="rows">
            {items.map((i, n) => (
              <li key={i.product_id ?? n} className="row row--receipt">
                <span className="name">
                  {i.name}
                  <span className="sub">
                    {i.quantity > 1 ? `${i.quantity} × ` : ""}
                    {i.available === false ? "unavailable" : "in the basket"}
                  </span>
                </span>
                <span className="price">{(Number(i.total) || 0).toFixed(2)}</span>
              </li>
            ))}
          </ul>

          {!items.length && <p className="empty">Your Migros basket is empty.</p>}

          {items.length > 0 && (
            <div className="meter">
              <div className="meter-row">
                <span className="meter-total">CHF {total.toFixed(2)}</span>
                <span className="meter-hint">
                  {met ? "Above the CHF 99 minimum" : `CHF ${(MINIMUM - total).toFixed(2)} to the minimum`}
                </span>
              </div>
              <div className="meter-track">
                <div className={`meter-fill${met ? " is-met" : ""}`}
                     style={{ width: `${Math.min(100, (total / MINIMUM) * 100)}%` }} />
              </div>
              {slot?.date && (
                <p className="meter-hint" style={{ textAlign: "left", maxWidth: "none", marginTop: 8 }}>
                  Slot held: {slot.weekday} {slot.date}, {slot.time}
                </p>
              )}
            </div>
          )}
        </>
      )}

      <button className="restore" type="button" style={{ marginTop: 12 }}
              onClick={load} disabled={state.loading}>
        {state.loading ? "Checking…" : "Refresh from Migros"}
      </button>
    </Card>
  );
}

/* -------------------------------------------------------------------- invite */

function HouseholdPanel({ onClose, members }) {
  const createInvite = useMutation(api.web.createInvite);
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    if (showInvite && !invite) createInvite().then(setInvite).catch(console.error);
  }, [showInvite]);

  const link = invite ? `${location.origin}/?join=${invite.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label="Your household">
      <div className="sheet-card">
        <h2 className="card-title">Your household</h2>

        <ul className="people">
          {members.map((m) => (
            <li key={m.userId} className="person">
              <span className={`me${m.isYou ? "" : " me--them"}`}>{m.initials}</span>
              <span className="person-who">
                {m.name}{m.isYou ? " (you)" : ""}
                <span className="sub">{m.email || (m.role === "owner" ? "Set up this household" : "Member")}</span>
              </span>
            </li>
          ))}
        </ul>

        {!showInvite ? (
          <button className="cta" type="button" onClick={() => setShowInvite(true)}>
            Invite someone
          </button>
        ) : invite ? (
          <>
            <div className="code">{invite.code}</div>
            <button className="cta" type="button" onClick={copy}>
              {copied ? "Link copied" : "Copy invite link"}
            </button>
            <p className="sheet-note">
              Expires in a week, works once. They see your list, kitchen and orders —
              their Migros account stays their own.
            </p>
          </>
        ) : (
          <p className="sheet-note">Making a code…</p>
        )}

        <button className="restore" type="button" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function ReceiptCard({ receipt, ...props }) {
  const when = new Date(receipt.purchasedAt).toLocaleDateString([], {
    day: "numeric", month: "short", year: "numeric",
  });
  const money = (c) => (c / 100).toFixed(2);

  return (
    <Card {...props} id={`receipt:${receipt._id}`} title={receipt.store}
          badge={<span className="badge badge--soft">{when}</span>}>
      <ul className="rows">
        {receipt.items.map((i) => (
          <li key={i._id} className="row row--receipt">
            <span className="name">
              {i.name}
              {i.quantity > 1 && <span className="sub">{i.quantity} ×</span>}
            </span>
            <span className="price">{money(i.totalCents)}</span>
          </li>
        ))}
      </ul>
      <div className="receipt-total">
        <span>{receipt.itemCount} items{receipt.where ? ` · ${receipt.where}` : ""}</span>
        <strong>{receipt.currency} {money(receipt.totalCents)}</strong>
      </div>
    </Card>
  );
}

/* The context shelf. Deliberately local for now — nothing here reaches the
   agent yet; it is a place to collect what should eventually be fed to it. */
function HomeCard({ shelf, onPin, onClear, onDrop, data, ...props }) {
  const [draft, setDraft] = useState("");

  const suggestions = [
    data.list ? `Shopping: ${data.list.name} (${data.items.length} items)` : null,
    data.pantry.length ? `${data.pantry.filter((p) => p.level === "low" || p.level === "empty").length} things running out` : null,
    data.recipes.length ? `${data.recipes.length} saved recipes` : null,
    data.receipts?.length ? `Last shop: ${data.receipts[0].store}` : null,
  ].filter(Boolean);

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    onPin(v);
    setDraft("");
  };

  return (
    <Card {...props} id="home" title="Context shelf"
          badge={<span className="badge badge--soft">{shelf.length} pinned</span>}>
      <p className="shelf-note">
        Pin what matters this week — a diet, a guest, a budget. Not connected to the
        agent yet; it&apos;s a staging area for what it should know.
      </p>

      {shelf.length > 0 ? (
        <ul className="rows">
          {shelf.map((n) => (
            <li key={n.id} className="row row--pot">
              <span className="name">{n.text}<span className="sub">{n.saved ? "Saved" : "This session"}</span></span>
              <button className="ghost" type="button" onClick={() => onPin(n.text, n.id)}
                      disabled={n.saved}>{n.saved ? "Kept" : "Keep"}</button>
              <button className="kill" type="button" aria-label="Remove"
                      onClick={() => onDrop(n.id)}>✕</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty">Nothing pinned yet.</p>
      )}

      {suggestions.length > 0 && (
        <div className="chips shelf-chips">
          {suggestions.map((sug) => (
            <button key={sug} className="chip" type="button" onClick={() => onPin(sug)}>+ {sug}</button>
          ))}
        </div>
      )}

      <div className="adder">
        <input type="text" value={draft} placeholder="Pin a note for the agent"
               aria-label="Pin a note"
               onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }} />
        <button type="button" onClick={commit}>Pin</button>
      </div>

      {shelf.length > 0 && (
        <button className="restore" type="button" style={{ marginTop: 10 }} onClick={onClear}>
          Clear unsaved
        </button>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------- chat */

function Chat({ members }) {
  const messages = useQuery(api.web.messages) ?? [];
  const send = useMutation(api.web.sendMessage);
  const uploadUrl = useMutation(api.web.generateUploadUrl);
  const ask = useAction(api.agent.ask);
  const [thinking, setThinking] = useState(false);
  const [scope, setScope] = useState("household");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);       // "image" | "voice" | null
  const [recording, setRecording] = useState(false);
  const threadRef = useRef(null);
  const fileRef = useRef(null);
  const recorderRef = useRef(null);

  // Upload to Convex storage, then post the message that references it.
  const upload = async (blob, mediaType, caption) => {
    setBusy(mediaType === "image" ? "image" : "voice");
    try {
      const url = await uploadUrl();
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": blob.type || "application/octet-stream" },
        body: blob,
      });
      const { storageId } = await res.json();
      await send({ content: caption, scope, storageId, mediaType });

      // Let the agent see the photo or hear the memo.
      setThinking(true);
      try {
        const urlRes = await fetch(`${url.split("/api/storage")[0]}/api/storage/${storageId}`);
        const r = await ask({
          content: caption,
          scope,
          mediaUrl: urlRes.url,
          mediaType,
        });
        if (!r?.ok) console.error("agent:", r?.error);
      } finally {
        setThinking(false);
      }
    } catch (err) {
      console.error("upload failed", err);
      alert("That didn't upload. Try again?");
    } finally {
      setBusy(null);
    }
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) upload(file, "image", draft.trim() || "");
    setDraft("");
  };

  const toggleRecording = async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 0) upload(blob, "audio", "");
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      console.error("mic refused", err);
      alert("No microphone access. Allow it in your browser settings to record.");
    }
  };

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Household shows only what was actually shared. "Just me" is your private
  // thread — which is where every message written before scopes existed lives.
  const visible = messages.filter((m) =>
    scope === "household" ? m.scope === "household" : m.mine && m.scope !== "household"
  );

  const submit = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || thinking) return;
    setDraft("");
    await send({ content, scope });
    setThinking(true);
    try {
      const r = await ask({ content, scope });
      if (!r?.ok) console.error("agent:", r?.error);
    } catch (err) {
      console.error("agent call failed", err);
    } finally {
      setThinking(false);
    }
  };

  return (
    <aside className="chat">
      <div className="scope">
        <div className="seg" role="group" aria-label="Who can see this chat">
          {["household", "me"].map((s) => (
            <button key={s} type="button" aria-pressed={scope === s} onClick={() => setScope(s)}>
              {s === "household" ? "Household" : "Just me"}
            </button>
          ))}
        </div>
        <span className="scope-note">
          {scope === "household"
            ? members.length > 1
              ? `${members.filter((m) => !m.isYou).map((m) => m.name).join(", ")} sees this`
              : "Shared with your household"
            : "Only you see this"}
        </span>
      </div>

      <div className="thread" ref={threadRef} aria-live="polite">
        {!visible.length && (
          <p className="empty">
            {scope === "household"
              ? "Nothing shared with the household yet. Messages you send here are visible to everyone in it."
              : "Ask for a recipe, a list, or dinner for four."}
          </p>
        )}
        {visible.map((m) =>
          m.role === "assistant" ? (
            <div className="turn" key={m._id}>
              <div className="face" />
              <div className="bubble">{m.content}</div>
            </div>
          ) : m.mine ? (
            <div className="mine" key={m._id}>
              <Attachment m={m} />
              {m.content}
            </div>
          ) : (
            <div className="theirs" key={m._id}>
              <span className={`by by--${hueFor(m.author, members)}`}>{initials(m.author)}</span>
              <div className="bubble">
                <span className="theirs-name">{m.author}</span>
                <Attachment m={m} />
                {m.content}
              </div>
            </div>
          )
        )}
        {thinking && (
          <div className="turn">
            <div className="face is-busy" />
            <div className="typing"><i /><i /><i /></div>
          </div>
        )}
      </div>

      <form className="composer" onSubmit={submit}>
        <div className="entry entry--rich">
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickImage} />
          <button className="attach" type="button" aria-label="Attach a photo"
                  disabled={busy !== null} onClick={() => fileRef.current?.click()}>
            {busy === "image" ? "…" : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 17.5V7a4 4 0 118 0v10a2.5 2.5 0 11-5 0V8.5"
                      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            )}
          </button>
          <button className={`attach${recording ? " attach--rec" : ""}`} type="button"
                  aria-label={recording ? "Stop recording" : "Record a voice memo"}
                  disabled={busy === "image"} onClick={toggleRecording}>
            {busy === "voice" ? "…" : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2.2" />
                <path d="M5 11a7 7 0 0014 0M12 18v3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            )}
          </button>
          <textarea
            rows={1}
            value={draft}
            placeholder={recording ? "Recording… tap the mic to stop" : "Ask for a recipe, a list, dinner for four…"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); }
            }}
          />
          <button className="send" type="submit" aria-label="Send">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.6"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </form>
    </aside>
  );
}

/* ---------------------------------------------------------------------- app */

const CAT_OF = (key) =>
  key === "home" ? "home" :
  key === "basket" ? "basket" :
  key === "list" ? "list" :
  key === "pantry" ? "pantry" :
  key.startsWith("recipe:") ? "recipes" :
  key.startsWith("receipt:") ? "receipts" : "orders";

const CAT_NAME = {
  home: "Home", basket: "Basket", list: "Shopping", pantry: "Pantry",
  recipes: "Recipe", receipts: "Receipt", orders: "Order",
};

function Board() {
  // State first: the board query reads listId, so it must exist before the call.
  const [view, setView] = useState("all");
  const [listId, setListId] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [pane, setPane] = useState("board");   // mobile only: board | chat
  const [shelf, setShelf] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ff.shelf") || "[]"); } catch { return []; }
  });
  const [joinNote, setJoinNote] = useState(null);
  const [layout, setLayout] = useState({ cardOrder: [], collapsed: [], closed: [] });
  const [drag, setDrag] = useState({ key: null, over: null, armed: null });

  const me = useQuery(api.web.me);
  const data = useQuery(api.web.board, listId ? { listId } : {});
  const stored = useQuery(api.web.layout);
  const ensureUser = useMutation(api.web.ensureUser);
  const saveLayout = useMutation(api.web.saveLayout);
  const acceptInvite = useMutation(api.web.acceptInvite);
  const { signOut } = useClerk();

  useEffect(() => {
    if (me?.needsProvisioning || (me && !me.householdId) || me?.needsName) ensureUser();
  }, [me?.needsProvisioning, me?.householdId, me?.needsName]);

  useEffect(() => {
    if (stored) setLayout({
      cardOrder: stored.cardOrder ?? [],
      collapsed: stored.collapsed ?? [],
      closed: stored.closed ?? [],
    });
  }, [stored?._id, stored?.updatedAt]);

  // A ?join=CODE link drops you straight into that household.
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("join");
    if (!code || !me || me.needsProvisioning) return;
    acceptInvite({ code })
      .then((r) => {
        setJoinNote(r.ok ? `You joined ${r.householdName}.` : r.reason);
        history.replaceState({}, "", location.pathname);
      })
      .catch((e) => setJoinNote(String(e.message || e)));
  }, [me?.userId]);

  const applyLayout = (next) => { setLayout(next); saveLayout(next); };

  const writeShelf = (next) => {
    setShelf(next);
    try { localStorage.setItem("ff.shelf", JSON.stringify(next.filter((n) => n.saved))); } catch {}
  };

  const pinNote = (text, id) => {
    if (id) return writeShelf(shelf.map((n) => (n.id === id ? { ...n, saved: true } : n)));
    if (shelf.some((n) => n.text === text)) return;
    writeShelf([...shelf, { id: `${Date.now()}-${Math.random()}`, text, saved: false }]);
  };
  const dropNote = (id) => writeShelf(shelf.filter((n) => n.id !== id));
  const clearShelf = () => writeShelf(shelf.filter((n) => n.saved));

  if (me === undefined || data === undefined) return <p className="loading">Loading…</p>;
  if (!data) return <p className="loading">Setting up your household…</p>;

  const members = me?.members ?? [];

  const keys = [
    "home",
    "list",
    "basket",
    "pantry",
    ...data.recipes.map((r) => `recipe:${r._id}`),
    ...(data.receipts ?? []).map((r) => `receipt:${r._id}`),
    ...data.orders.map((o) => `order:${o._id}`),
  ];

  // stored order first, anything new appended
  const ordered = [
    ...layout.cardOrder.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !layout.cardOrder.includes(k)),
  ];

  const dragApi = {
    key: drag.key,
    over: drag.over,
    armed: drag.armed,
    arm: (id) => setDrag((d) => ({ ...d, armed: id })),
    start: (id) => setDrag((d) => ({ ...d, key: id })),
    end: () => setDrag({ key: null, over: null, armed: null }),
    setOver: (id) => setDrag((d) => ({ ...d, over: id })),
    drop: (id) => {
      const from = ordered.indexOf(drag.key);
      const to = ordered.indexOf(id);
      setDrag({ key: null, over: null, armed: null });
      if (from < 0 || to < 0 || from === to) return;
      const next = [...ordered];
      next.splice(to, 0, next.splice(from, 1)[0]);
      applyLayout({ ...layout, cardOrder: next });
    },
  };

  const shown = ordered.filter(
    (k) => (view === "all" || CAT_OF(k) === view) && !layout.closed.includes(k)
  );
  const hidden = ordered.filter(
    (k) => (view === "all" || CAT_OF(k) === view) && layout.closed.includes(k)
  );

  const shared = { layout, onLayout: applyLayout, drag: dragApi, members };

  const tabs = [
    { key: "all", label: "Everything", count: "" },
    { key: "home", label: "Home", count: shelf.length || "" },
    { key: "list", label: "Shopping", count: data.items.filter((i) => !i.checked && !i.ordered).length },
    { key: "basket", label: "Basket", count: "" },
    { key: "pantry", label: "Pantry", count: data.pantry.length },
    { key: "recipes", label: "Recipes", count: data.recipes.length },
    { key: "receipts", label: "Receipts", count: (data.receipts ?? []).length },
    { key: "orders", label: "Orders", count: data.orders.length },
  ];

  const render = (key) => {
    if (key === "home") return (
      <HomeCard key={key} shelf={shelf} data={data} onPin={pinNote}
                onDrop={dropNote} onClear={clearShelf} {...shared} />
    );
    if (key === "basket") return <BasketCard key={key} {...shared} />;
    if (key.startsWith("receipt:")) {
      const rc = (data.receipts ?? []).find((x) => `receipt:${x._id}` === key);
      return rc ? <ReceiptCard key={key} receipt={rc} {...shared} /> : null;
    }
    if (key === "list") return (
      <ShoppingCard key={key} items={data.items} lists={data.lists ?? []}
                    list={data.list} onPickList={setListId} {...shared} />
    );
    if (key === "pantry") return <KitchenCard key={key} pantry={data.pantry} {...shared} />;
    if (key.startsWith("recipe:")) {
      const r = data.recipes.find((x) => `recipe:${x._id}` === key);
      return r ? <RecipeCard key={key} recipe={r} {...shared} /> : null;
    }
    const o = data.orders.find((x) => `order:${x._id}` === key);
    return o ? <OrderCard key={key} order={o} {...shared} /> : null;
  };

  return (
    <div className={`shell pane-${pane}`}>
      <header className="topbar">
        <h1 className="wordmark">fresh<span>fully</span></h1>
        <div className="where">Migros, delivered</div>
        <button className="household" type="button" title="Your household"
                aria-label={`Household: ${members.map((m) => m.name).join(", ")}`}
                onClick={() => setInviting(true)}>
          {members.map((m, n) => (
            <span key={m.userId} className={`me${n ? " me--them" : ""}`}>{m.initials}</span>
          ))}
          <span className="me me--add">+</span>
        </button>
        <button className="signout" type="button" onClick={() => signOut()}>Sign out</button>
      </header>

      {joinNote && (
        <p className="joined" onClick={() => setJoinNote(null)}>{joinNote}</p>
      )}
      {inviting && <HouseholdPanel members={members} onClose={() => setInviting(false)} />}

      <div className="workspace">
        <nav className="rail" aria-label="Categories">
          {tabs.map((t) => (
            <button key={t.key} className="tab" type="button"
                    aria-pressed={view === t.key}
                    onClick={() => { setView(t.key); setPane("board"); }}>
              <span className="tab-icon"><Icon name={t.key} /></span>
              <span>{t.label}</span>
              <span className="tab-count">{t.count}</span>
            </button>
          ))}
        </nav>

        <main className="board" aria-label="Board">
          {shown.map(render)}

          {view === "recipes" && !data.recipes.length && (
            <p className="empty">No recipes yet. Ask for one in the chat and it lands here.</p>
          )}
          {view === "receipts" && !(data.receipts ?? []).length && (
            <p className="empty">No receipts yet. Photograph one in the chat and it lands here.</p>
          )}
          {view === "orders" && !data.orders.length && (
            <p className="empty">No orders yet. When either of you orders, it shows up here.</p>
          )}

          {hidden.length > 0 && (
            <div className="hidden-bar">
              <span>Hidden</span>
              {hidden.map((k) => (
                <button key={k} className="restore" type="button"
                        onClick={() => applyLayout({ ...layout, closed: layout.closed.filter((x) => x !== k) })}>
                  {CAT_NAME[CAT_OF(k)]}
                </button>
              ))}
              {hidden.length > 1 && (
                <button className="restore" type="button"
                        onClick={() => applyLayout({ ...layout, closed: [] })}>
                  Bring back all
                </button>
              )}
            </div>
          )}
        </main>

        <Chat members={members} />

        <nav className="switcher" aria-label="Board or chat">
          <button type="button" aria-pressed={pane === "board"}
                  onClick={() => setPane("board")}>Board</button>
          <button type="button" aria-pressed={pane === "chat"}
                  onClick={() => setPane("chat")}>Chat</button>
        </nav>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <>
      <SignedOut>
        <div className="gate">
          <div className="gate-inner">
            <h1>fresh<span style={{ color: "var(--leaf)" }}>fully</span></h1>
            <p>Your shopping list, your kitchen and your orders — shared with the people you live with.</p>
            <SignInButton mode="modal">
              <button className="cta" type="button">Sign in</button>
            </SignInButton>
          </div>
        </div>
      </SignedOut>
      <SignedIn>
        <Board />
      </SignedIn>
    </>
  );
}
